import {
  benefitBreakdownSearch,
  facilitySearch,
  getNetworkTiers,
  procedureSearch,
  providerCostSearch,
  providerSearch,
  specialitySearch,
} from "@/services/provider-search-pt";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
} from "@tanstack/react-query";
import type { IUserInfo } from "@/hooks/use-user-info";
import { getLocation } from "@/services/location";

import type { AxiosError } from "axios";
import { QueryKeys, ResponseCode } from "@/constants";
import { ErrorNotificationType } from "@/pages/components/error-notification";
import { emitEvent, EventConstants } from "@/lib/event-emitter";
import type GenericResponse from "@/model/generic-response";

import type BenefitBreakdown from "@/model/benefit-break-down";
import type Speciality from "@/model/speciality";
import type Network from "@/model/network";

import type { ICostEstFilterParams } from "@/hooks/use-cost-est-filter";
import type { SpendSummaryAccumulatorWithTier } from "@/model/spend-summary";
import { useEffect } from "react";
import hash from "object-hash";

export type ISpecialityResult = {
  default: Speciality[];
  all: Speciality[];
};
export type INetworkResult = {
  tiers: { name: string }[];
  networks: Network[];
};

type ICostEstimatorResultParams = {
  userInfo: IUserInfo;
  accumulators: SpendSummaryAccumulatorWithTier[];
  filter: ICostEstFilterParams;
  pageParam: number;
};

export const useCostEstimatorDefaults = (
  info: IUserInfo,
  callbacks?: {
    onNetworksSuccess?: (data: INetworkResult) => void;
    onBenefitBreakdownSuccess?: (data: BenefitBreakdown[]) => void;
    onAllQueriesComplete?: () => void;
  },
): [
  UseQueryResult<INetworkResult, Error>,
  UseQueryResult<BenefitBreakdown[], Error>,
] => {
  const results = useQueries({
    queries: [
      {
        queryKey: [QueryKeys.NETWORKS, info.username],
        queryFn: async () => {
          const response = await getNetworkTiers(info);
          const tiers = [];
          const networks = [];
          for (const key in response) {
            tiers.push({ name: key });
            const tempNetwork = (response[key] as Network[]).map((networks) => {
              return { ...networks, tier: key };
            });
            networks.push(tempNetwork);
          }

          return {
            tiers,
            networks: networks.flat(),
          };
        },
        enabled: Boolean(info.username) && Boolean(info.accessToken),
        staleTime: 1000 * 60 * 60, // 1 hour
        gcTime: 1000 * 60 * 60, // 1 hour
      },
      {
        queryKey: [QueryKeys.BENEFIT_BREAKDOWN, info.username],
        queryFn: () => benefitBreakdownSearch(info),
        enabled: Boolean(info.username) && Boolean(info.accessToken),
        staleTime: 1000 * 60 * 60, // 1 hour
        gcTime: 1000 * 60 * 60, // 1 hour
      },
    ],
  });
  const [tiersQuery, benefitBreakdownQuery] = results;

  useEffect(() => {
    if (tiersQuery.isSuccess && callbacks?.onNetworksSuccess) {
      callbacks.onNetworksSuccess(tiersQuery.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiersQuery.isSuccess, tiersQuery.data]);

  useEffect(() => {
    if (
      benefitBreakdownQuery.isSuccess &&
      callbacks?.onBenefitBreakdownSuccess
    ) {
      callbacks.onBenefitBreakdownSuccess(benefitBreakdownQuery.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [benefitBreakdownQuery.isSuccess, benefitBreakdownQuery.data]);

  return results;
};
const useSpecialitiesSearch = (
  userInfo: IUserInfo,
  queryString: string | null,
  callbacks?: {
    onSpecialitiesSuccess?: (data: ISpecialityResult) => void;
  },
): UseQueryResult<ISpecialityResult, Error> => {
  const result = useQuery({
    queryKey: [QueryKeys.SPECIALITIES, userInfo.username, queryString],
    queryFn: async () => {
      const response = await specialitySearch(queryString!, userInfo);

      const transformedSpecialities = {
        default: response.filter(
          (speciality: Speciality) => speciality.ranking !== "OTHERS",
        ),
        all: response,
      };

      return transformedSpecialities;
    },

    enabled:
      Boolean(userInfo.username) &&
      Boolean(userInfo.accessToken) &&
      Boolean(queryString),
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60, // 1 hour
  });

  useEffect(() => {
    if (result.isSuccess && callbacks?.onSpecialitiesSuccess) {
      callbacks.onSpecialitiesSuccess(result.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.isSuccess, result.data]);

  return result;
};

const useProcedureSearch = <T>(
  userInfo: IUserInfo,
): UseMutationResult<
  GenericResponse<T>,
  Error,
  { queryString: string; url?: string }
> => {
  return useMutation<
    GenericResponse<T>,
    Error,
    { queryString: string; url?: string }
  >({
    mutationKey: [QueryKeys.PROCEDURE_SEARCH],
    mutationFn: ({ queryString, url }) =>
      procedureSearch(queryString, userInfo, url),
    onError: (error) => {
      if ((error as AxiosError).response) {
        switch ((error as AxiosError).response?.status) {
          case ResponseCode.UNAUTHORIZED: {
            const err1 = (error as AxiosError).response?.data as {
              message: string;
            };
            emitEvent(EventConstants.ADD_ERROR, {
              message: err1.message,
              type: ErrorNotificationType.ERROR,
            });
            break;
          }
          default:
            break;
        }
      }
    },
  });
};

const useLocationSearch = <T>(
  userInfo: IUserInfo,
): UseMutationResult<GenericResponse<T>, Error, { queryString: string }> => {
  return useMutation<GenericResponse<T>, Error, { queryString: string }>({
    mutationKey: [QueryKeys.LOCATION_SEARCH],
    mutationFn: ({ queryString }) => getLocation(queryString, userInfo),
    onError: (error) => {
      if ((error as AxiosError).response) {
        switch ((error as AxiosError).response?.status) {
          case ResponseCode.UNAUTHORIZED: {
            const err1 = (error as AxiosError).response?.data as {
              message: string;
            };
            emitEvent(EventConstants.ADD_ERROR, {
              message: err1.message,
              type: ErrorNotificationType.ERROR,
            });
            break;
          }
          default:
            break;
        }
      }
    },
  });
};

const useProviderSearch = <T>(
  userInfo: IUserInfo,
): UseMutationResult<
  GenericResponse<T>,
  Error,
  {
    queryString: string;
    filter: ICostEstFilterParams;
    url?: string;
  }
> => {
  return useMutation<
    GenericResponse<T>,
    Error,
    {
      queryString: string;
      filter: ICostEstFilterParams;
      url?: string;
    }
  >({
    mutationKey: [QueryKeys.PROVIDER_SEARCH],
    mutationFn: ({ queryString, filter, url }) =>
      providerSearch(queryString, userInfo, filter, url),
    onError: (error) => {
      if ((error as AxiosError).response) {
        switch ((error as AxiosError).response?.status) {
          case ResponseCode.UNAUTHORIZED: {
            const err1 = (error as AxiosError).response?.data as {
              message: string;
            };
            emitEvent(EventConstants.ADD_ERROR, {
              message: err1.message,
              type: ErrorNotificationType.ERROR,
            });
            break;
          }
          default:
            break;
        }
      }
    },
  });
};

const useFacilitySearch = <T>(
  userInfo: IUserInfo,
): UseMutationResult<
  GenericResponse<T>,
  Error,
  {
    queryString: string;
    filter: ICostEstFilterParams;
    url?: string;
  }
> => {
  return useMutation<
    GenericResponse<T>,
    Error,
    {
      queryString: string;
      filter: ICostEstFilterParams;
      url?: string;
    }
  >({
    mutationKey: [QueryKeys.FACILITY_SEARCH],
    mutationFn: ({ queryString, filter, url }) =>
      facilitySearch(queryString, userInfo, filter, url),
    onError: (error) => {
      if ((error as AxiosError).response) {
        switch ((error as AxiosError).response?.status) {
          case ResponseCode.UNAUTHORIZED: {
            const err1 = (error as AxiosError).response?.data as {
              message: string;
            };
            emitEvent(EventConstants.ADD_ERROR, {
              message: err1.message,
              type: ErrorNotificationType.ERROR,
            });
            break;
          }
          default:
            break;
        }
      }
    },
  });
};

const useProviderCostSearchInfinite = (
  userInfo: IUserInfo,
  accumulators: SpendSummaryAccumulatorWithTier[],
  filter: ICostEstFilterParams,
  enabledInfinite: boolean = false,
) => {
  return useInfiniteQuery({
    queryKey: [QueryKeys.PROVIDER_COST_SEARCH, hash(filter)],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await providerCostSearch(
        userInfo,
        accumulators,
        filter,
        pageParam,
      );
      return response;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (Boolean(lastPage.next) && enabledInfinite) {
        const pageCount = allPages.length;
        return pageCount * 10;
      } else {
        return undefined;
      }
    },
    enabled:
      Boolean(accumulators.length) &&
      Boolean(userInfo.accessToken) &&
      Boolean(filter.procedure) &&
      Boolean(filter.targetLocation) &&
      Boolean(filter.distance) &&
      Boolean(filter.tiers) &&
      Boolean(filter.specialities),
  });
};

const useCostEstimatorResultDownload = <T>() => {
  return useMutation<GenericResponse<T>, Error, ICostEstimatorResultParams>({
    mutationFn: ({ userInfo, accumulators, filter, pageParam }) =>
      providerCostSearch(userInfo, accumulators, filter, pageParam),
  });
};

export {
  useProcedureSearch,
  useLocationSearch,
  useProviderSearch,
  useFacilitySearch,
  useSpecialitiesSearch,
  useProviderCostSearchInfinite,
  useCostEstimatorResultDownload,
};
